
import { Week, Day, Exercise } from "../types/workout-types";

interface ProgramDataDay {
  day_number?: number;
  focus?: string;
  title?: string;
  subtitle?: string;
  session_type?: string;
  exercises?: Array<{
    name: string;
    sets: number | string;
    reps: string | number;
    intensity?: string;
    rest_seconds?: number;
    weight?: string;
    rpe?: string;
    rest?: string;
    notes?: string;
    exercise_notes?: string;
  }>;
}

interface ProgramDataWeek {
  week_number?: number;
  days: ProgramDataDay[];
}

export interface ProgramData {
  weekly_schedule?: ProgramDataWeek[];
  days?: ProgramDataDay[];
  [key: string]: any;
}

/**
 * Maps an API day object to our internal Day format
 */
export const mapDay = (day: ProgramDataDay, weekIndex: number, dayIndex: number): Day => {
  const dayNumber = day.day_number || dayIndex + 1;
  
  const mappedExercises: Exercise[] = day.exercises 
    ? day.exercises.map((apiExercise, exerciseIndex) => {
        return {
          id: `exercise-${weekIndex}-${dayIndex}-${exerciseIndex}`,
          exercise: apiExercise.name,
          sets: typeof apiExercise.sets === 'number' ? apiExercise.sets.toString() : apiExercise.sets || "",
          reps: apiExercise.reps?.toString() || "",
          kgs: apiExercise.weight?.toString() || "",
          rpe: apiExercise.rpe || apiExercise.intensity || "",
          rest: apiExercise.rest || (apiExercise.rest_seconds ? apiExercise.rest_seconds.toString() : ""),
          notes: apiExercise.notes || "",
          exercise_notes: apiExercise.exercise_notes || "",
          name: apiExercise.name,
          intensity: apiExercise.intensity,
          rest_seconds: apiExercise.rest_seconds
        };
      })
    : [];
  
  return {
    id: `day-${dayNumber}`,
    title: day.title || day.focus || `Day ${dayNumber}`,
    subtitle: day.subtitle || `Week ${weekIndex + 1} - Day ${dayNumber}`,
    day_number: dayNumber,
    focus: day.focus,
    session_type: day.session_type,
    exercises: mappedExercises,
    notes: ""
  };
};

/**
 * Maps a weekly schedule from API format to our internal format
 */
export const mapWeeklySchedule = (weeklySchedule: ProgramDataWeek[]): Week[] => {
  return weeklySchedule.map((week, weekIndex) => {
    return {
      id: `week-${week.week_number || weekIndex + 1}`,
      title: `Week ${week.week_number || weekIndex + 1}`,
      week_number: week.week_number || weekIndex + 1,
      days: week.days.map((day: ProgramDataDay, dayIndex: number) => {
        return mapDay(day, weekIndex, dayIndex);
      })
    };
  });
};

/**
 * Maps a flat days array to week structure
 */
export const mapDaysArray = (days: ProgramDataDay[]): Week[] => {
  const daysPerWeek = 7;
  const groupedWeeks: Week[] = [];
  
  for (let i = 0; i < days.length; i += daysPerWeek) {
    const weekDays = days.slice(i, i + daysPerWeek);
    const weekNumber = Math.floor(i / daysPerWeek) + 1;
    
    groupedWeeks.push({
      id: `week-${weekNumber}`,
      title: `Week ${weekNumber}`,
      week_number: weekNumber,
      days: weekDays.map((day, dayIndex) => mapDay(day, weekNumber - 1, dayIndex))
    });
  }
  
  return groupedWeeks;
};

/**
 * Process raw program data into our internal format
 */
export const processData = (data: any): Week[] => {
  if (!data) return [];
  
  const parsedData = data as ProgramData;
  let fetchedWeeks: Week[] = [];
  
  if (parsedData.weekly_schedule && Array.isArray(parsedData.weekly_schedule)) {
    fetchedWeeks = mapWeeklySchedule(parsedData.weekly_schedule);
  } 
  else if (parsedData.days && Array.isArray(parsedData.days)) {
    fetchedWeeks = mapDaysArray(parsedData.days);
  }
  
  return fetchedWeeks;
};
